import { Controller } from "@hotwired/stimulus"

// Links the four parts of the homepage track-completeness panel: hovering (or
// focusing) a corner in the rail lights that corner's dot on the map, names it
// in the caption, highlights the mini-sectors it owns in the lap ribbon, and
// names it in the copy beside the panel.
//
// The rail buttons carry everything needed:
//   data-index    — position, matching the dot with the same index
//   data-label    — "Corner 9 · Pouhon"
//   data-sections — comma-separated mini-sector indices this corner overlaps,
//                   computed server-side from the real corner window against
//                   the real section boundaries
//
// Rail entries are <button>s so the panel is reachable by keyboard; focus and
// blur drive the same path as mouseenter and mouseleave.
export default class extends Controller {
  static targets = [
    "rail",
    "dot",
    "bar",
    "caption",
    "ribbonNote",
    "pitchName",
    "pitchSpan",
  ]

  connect() {
    this.reset()
  }

  activate(event) {
    const rail = event.currentTarget
    const index = Number(rail.dataset.index)
    const sections = (rail.dataset.sections || "")
      .split(",")
      .filter((value) => value !== "")
      .map(Number)

    this.#paintRail(index)
    this.#paintDots(index)
    this.#paintBars(sections)

    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = rail.dataset.label
      this.captionTarget.classList.add("text-accent-lavender-bright")
      this.captionTarget.classList.remove("text-text-muted")
    }

    if (this.hasRibbonNoteTarget) {
      this.ribbonNoteTarget.textContent = this.#ribbonSummary(sections)
      this.ribbonNoteTarget.classList.add("text-accent-primary")
      this.ribbonNoteTarget.classList.remove("text-text-secondary")
    }

    const name = rail.querySelector("[data-corner-name]")?.textContent
    if (this.hasPitchNameTarget && name) this.pitchNameTarget.textContent = name
    if (this.hasPitchSpanTarget)
      this.pitchSpanTarget.textContent = this.#pitchSpan(sections)
  }

  reset() {
    this.#paintRail(null)
    this.#paintDots(null)
    this.#paintBars([])

    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = "Hover a corner"
      this.captionTarget.classList.remove("text-accent-lavender-bright")
      this.captionTarget.classList.add("text-text-muted")
    }

    if (this.hasRibbonNoteTarget) {
      this.ribbonNoteTarget.textContent =
        "Hover a corner to see which mini-sectors it owns"
      this.ribbonNoteTarget.classList.remove("text-accent-primary")
      this.ribbonNoteTarget.classList.add("text-text-secondary")
    }

    // The copy keeps the first corner as its resting example, so the sentence
    // reads as a complete claim before anyone touches the panel.
    const first = this.railTargets[0]
    if (first) {
      const name = first.querySelector("[data-corner-name]")?.textContent
      if (this.hasPitchNameTarget && name)
        this.pitchNameTarget.textContent = name
      if (this.hasPitchSpanTarget) {
        const sections = (first.dataset.sections || "")
          .split(",")
          .filter((v) => v !== "")
        this.pitchSpanTarget.textContent = this.#pitchSpan(sections.map(Number))
      }
    }
  }

  // ── Private ─────────────────────────────────────────────────────────────

  #paintRail(activeIndex) {
    this.railTargets.forEach((rail) => {
      const active = Number(rail.dataset.index) === activeIndex
      rail.classList.toggle("bg-accent-lavender/16", active)

      const number = rail.querySelector("[data-corner-number]")
      if (number) {
        number.classList.toggle("text-accent-lavender-bright", active)
        number.classList.toggle("text-text-muted", !active)
      }
      const name = rail.querySelector("[data-corner-name]")
      if (name) name.classList.toggle("text-white", active)
      const dist = rail.querySelector("[data-corner-dist]")
      if (dist) {
        dist.classList.toggle("text-accent-lavender", active)
        dist.classList.toggle("text-text-secondary", !active)
      }
    })
  }

  // Colour is toggled as classes, not as fill/stroke attributes: the circles
  // carry Tailwind fill-*/stroke-* utilities and a CSS class outranks an SVG
  // presentation attribute, so setAttribute("fill", …) would be ignored.
  // Radius and stroke width have no utility equivalent and stay attributes.
  #paintDots(activeIndex) {
    this.dotTargets.forEach((dot) => {
      const active = Number(dot.dataset.index) === activeIndex
      dot.setAttribute("r", active ? "3.6" : "1.9")
      dot.setAttribute("stroke-width", active ? "1.2" : "0.8")
      dot.classList.toggle("fill-accent-primary", active)
      dot.classList.toggle("fill-accent-lavender/50", !active)
    })
  }

  #paintBars(sections) {
    const lit = new Set(sections)
    this.barTargets.forEach((bar) => {
      const index = Number(bar.dataset.index)
      const sector = Number(bar.dataset.sector)
      const on = lit.has(index)

      bar.classList.toggle("bg-accent-primary", on)
      bar.classList.toggle("bg-accent-lavender/16", !on && sector === 1)
      bar.classList.toggle("bg-accent-lavender/11", !on && sector === 2)
      bar.classList.toggle("bg-accent-lavender/7", !on && sector >= 3)
    })
  }

  #ribbonSummary(sections) {
    if (sections.length === 0) return "No mini-sector recorded for this corner"

    const noun = sections.length === 1 ? "mini-sector" : "mini-sectors"
    return `${sections.length} ${noun} of ${this.barTargets.length}`
  }

  #pitchSpan(sections) {
    if (sections.length <= 1) return "its own mini-sector"

    return `${sections.length} mini-sectors`
  }
}
